import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';
import { useProfileMode } from '@/state/profileMode';
import { getAuthRedirectUrl, isMobileWebView } from '@/utils/authRedirect';
import { ensureUserProfile } from '@/utils/authProfile';
import { setActiveUserId } from '@/storage/activeUser';
import { clearLegacyUnscopedWebKeys, clearUserScopedWebStorage } from '@/storage/userScopedStorage';
import { useExploreFilters } from '@/state/exploreFilters';
import { useDrafts } from '@/state/drafts';

type AuthCtx = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
};

const SESSION_SOFT_FALLBACK_MS = 8_000;
const SESSION_HARD_FALLBACK_MS = 10_000;

function getSupabaseStorageKey(): string | null {
  try {
    const url = env.supabase.url || undefined;
    const projectRef = url ? url.split('//')[1]?.split('.')[0] : undefined;
    return projectRef ? `sb-${projectRef}-auth-token` : null;
  } catch {
    return null;
  }
}

function normalizeStoredSession(value: unknown): Session | null {
  const container = value as any;
  const candidate = container?.currentSession ?? container?.session ?? container;
  if (!candidate || typeof candidate !== 'object') return null;
  if (typeof candidate.access_token !== 'string') return null;
  if (!candidate.user?.id) return null;
  return candidate as Session;
}

export function readStoredAuthSession(): Session | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const storageKey = getSupabaseStorageKey();
    const raw = storageKey ? localStorage.getItem(storageKey) : null;
    if (!raw) return null;
    return normalizeStoredSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

function isRecoverableSessionError(err: any): boolean {
  const name = String(err?.name ?? '').toLowerCase();
  const message = String(err?.message ?? err ?? '').toLowerCase();
  return (
    name.includes('abort') ||
    message.includes('abort') ||
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('network') ||
    message.includes('fetch failed') ||
    message.includes('failed to fetch') ||
    message.includes('load failed') ||
    message.includes('blocked') ||
    message.includes('err_blocked')
  );
}

const Ctx = createContext<AuthCtx>({ 
  session: null, 
  user: null, 
  loading: true,
  signUp: async () => ({ data: null, error: null }),
  signIn: async () => ({ data: null, error: null }),
  signOut: async () => ({ error: null })
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthCtx['session']>(null);
  const [user, setUser] = useState<AuthCtx['user']>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null>(null);

  const applyUserContext = async (nextUserId: string | null) => {
    const prevUserId = prevUserIdRef.current;
    if (prevUserId === nextUserId) return;

    prevUserIdRef.current = nextUserId;

    // Always clear legacy unscoped keys (ambiguous ownership → delete).
    clearLegacyUnscopedWebKeys();

    // Set active user for user-scoped storages (zustand persist, userLocalStorage, etc.)
    setActiveUserId(nextUserId);

    // Hard reset all runtime caches on user switch (prevents any cross-user reads).
    qc.clear();

    // Rehydrate per-user stores only when we have a userId.
    if (nextUserId) {
      useExploreFilters.getState().rehydrateForUser(nextUserId);
      useProfileMode.getState().rehydrateForUser(nextUserId);
      try {
        // zustand persist does not rehydrate automatically after we change the storage namespace.
        await (useDrafts as any).persist?.rehydrate?.();
      } catch {
        // ignore
      }
    } else {
      // Signed out: clear runtime-only views.
      if (prevUserId) {
        // Spec: on logout, remove ALL user-scoped persisted keys for the previous user.
        clearUserScopedWebStorage(prevUserId);
      }
      useExploreFilters.getState().reset();
      useProfileMode.getState().setMode('usuario');
      try {
        (useDrafts.getState() as any).clearAll?.();
      } catch {}
    }
  };

  useEffect(() => {
    let mounted = true;
    let sessionResolved = false;
    let softFallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let hardFallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const clearSessionTimers = () => {
      if (softFallbackTimer) {
        clearTimeout(softFallbackTimer);
        softFallbackTimer = null;
      }
      if (hardFallbackTimer) {
        clearTimeout(hardFallbackTimer);
        hardFallbackTimer = null;
      }
    };

    const applySessionState = async (nextSession: Session | null) => {
      if (!mounted) return;
      const nextUserId = nextSession?.user?.id ?? null;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
      clearSessionTimers();
      try {
        await applyUserContext(nextUserId);
      } catch (err) {
        console.warn('[AuthProvider] Error aplicando contexto de usuario:', err);
      }
      if (!nextUserId) {
        useProfileMode.getState().setMode("usuario");
      }
    };

    const revalidateStoredSession = (source: string, candidate: Session) => {
      void (async () => {
        try {
          const { data, error } = await supabase.auth.getUser();
          if (!mounted) return;

          if (error) {
            if (isRecoverableSessionError(error)) return;
            console.warn(`[AuthProvider] ${source}: sesion local invalida; limpiando estado.`, error);
            await applySessionState(null);
            return;
          }

          if (!data.user?.id || data.user.id !== candidate.user.id) {
            console.warn(`[AuthProvider] ${source}: usuario local no coincide con Supabase; limpiando estado.`);
            await applySessionState(null);
          }
        } catch (err) {
          if (isRecoverableSessionError(err)) return;
          console.warn(`[AuthProvider] ${source}: no se pudo validar la sesion local; limpiando estado.`, err);
          await applySessionState(null);
        }
      })();
    };

    const applyStoredSessionFallback = (source: string): boolean => {
      const storedSession = readStoredAuthSession();
      if (!storedSession) return false;
      if (import.meta.env.DEV) {
        console.warn(`[AuthProvider] ${source}: getSession lento; conservando sesión local mientras Supabase responde.`);
      }
      void applySessionState(storedSession);
      revalidateStoredSession(source, storedSession);
      return true;
    };

    softFallbackTimer = setTimeout(() => {
      if (!mounted || sessionResolved) return;
      applyStoredSessionFallback('getSession_soft_timeout');
    }, SESSION_SOFT_FALLBACK_MS);

    hardFallbackTimer = setTimeout(() => {
      if (!mounted || sessionResolved) return;
      if (applyStoredSessionFallback('getSession_hard_timeout')) return;
      if (import.meta.env.DEV) {
        console.warn('[AuthProvider] Timeout de seguridad en getSession() sin sesión local; liberando carga.');
      }
      setLoading(false);
    }, SESSION_HARD_FALLBACK_MS);

    (async () => {
      try {
        const result = await supabase.auth.getSession();
        sessionResolved = true;
        clearSessionTimers();
        if (!mounted) return;
        const { data, error } = result;
        
        if (error) {
          if (isRecoverableSessionError(error) && applyStoredSessionFallback('getSession_recoverable_error')) {
            return;
          }
          if (isRecoverableSessionError(error)) {
            if (import.meta.env.DEV) {
              console.warn('[AuthProvider] Error recuperable en getSession() sin sesión local; continuando sin usuario.', error);
            }
          } else if (import.meta.env.DEV) {
            console.error('[AuthProvider] Error en getSession():', error);
          }
        }
        await applySessionState(data?.session ?? null);
      } catch (err: any) {
        sessionResolved = true;
        clearSessionTimers();
        if (!mounted) return;
        if (isRecoverableSessionError(err) && applyStoredSessionFallback('getSession_exception_recoverable')) {
          return;
        }
        if (isRecoverableSessionError(err)) {
          if (import.meta.env.DEV) {
            console.warn('[AuthProvider] Timeout/bloqueo en getSession() sin sesión local; continuando sin usuario.', err);
          }
        } else {
          if (import.meta.env.DEV) {
            console.error('[AuthProvider] Excepción en getSession():', err);
          }
        }
        await applySessionState(null);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((evt, sess) => {
      if (!mounted) return;
      
      // 🔄 Detectar errores de refresh token
      if (evt === 'TOKEN_REFRESHED' && !sess) {
        const storedSession = readStoredAuthSession();
        if (storedSession) {
          console.warn('[AuthProvider] TOKEN_REFRESHED sin sesión pero existe sesión local; conservando usuario hasta confirmación.');
          setSession((current) => current ?? storedSession);
          setUser((current) => current ?? storedSession.user ?? null);
          setLoading(false);
          void applyUserContext(storedSession.user?.id ?? null);
          revalidateStoredSession('token_refreshed_null', storedSession);
          return;
        }
        // Si el refresh falló y no hay sesión, limpiar estado
        console.warn('[AuthProvider] Token refresh falló, limpiando sesión...');
        setSession(null);
        setUser(null);
        setLoading(false);
        useProfileMode.getState().setMode("usuario");
        return;
      }
      
      setSession(sess ?? null);
      setUser(sess?.user ?? null);
      setLoading(false);
      // ⚠️ IMPORTANTE:
      // No bloquear este callback con awaits largos. GoTrue mantiene un lock interno
      // durante onAuthStateChange; si tardamos >5s aparecen warnings de lock huérfano.
      // Ejecutamos trabajo pesado de forma diferida.
      void (async () => {
        try {
          // ✅ Single source of truth: on ANY userId change, fully reset + rehydrate.
          await applyUserContext(sess?.user?.id ?? null);
        } catch (err) {
          console.warn('[AuthProvider] Error aplicando contexto de usuario:', err);
        }

        // 🔁 Invalidar queries de perfiles cuando cambia auth
        if (sess?.user) {
          try {
            await qc.invalidateQueries({ queryKey: ["profile"] });
            await qc.invalidateQueries({ queryKey: ["profile", "me", sess.user.id] });
            await qc.invalidateQueries({ queryKey: ["academy"] });
            await qc.invalidateQueries({ queryKey: ["academy", "my"] });
            await qc.invalidateQueries({ queryKey: ["academy", "mine"] });
            await qc.invalidateQueries({ queryKey: ["organizer"] });
            await qc.invalidateQueries({ queryKey: ["organizer", "mine"] });
            await qc.invalidateQueries({ queryKey: ["teacher"] });
            await qc.invalidateQueries({ queryKey: ["teacher", "mine"] });
            await qc.invalidateQueries({ queryKey: ["brand"] });
            await qc.invalidateQueries({ queryKey: ["brand", "mine"] });
          } catch (err) {
            console.warn('[AuthProvider] Error invalidando queries:', err);
          }
        }
      })();

      // 🔍 Identificar usuario en Hotjar cuando hay sesión (lazy loaded, no bloquea)
      if (sess?.user && evt === 'SIGNED_IN') {
        // Lazy load Hotjar identify (no bloquea el render)
        import('../lib/hotjar').then(({ identifyUser }) => {
          identifyUser(sess.user.id, {
            email: sess.user.email,
            created_at: sess.user.created_at,
          });
        }).catch(() => {
          // Silently fail if Hotjar not loaded yet
        });
      }
      
      // 🎭 Resetear a usuario si se cierra sesión
      if (!sess?.user) {
        useProfileMode.getState().setMode("usuario");
      }
    });

    return () => { 
      mounted = false;
      clearSessionTimers();
      sub.subscription.unsubscribe(); 
    };
  }, [qc]);

  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getAuthRedirectUrl(),
      },
    });
    if (!error && data.user && data.session) {
      try {
        await ensureUserProfile(data.user);
      } catch (profileError) {
        console.warn('[AuthProvider] No se pudo asegurar perfil tras registro:', profileError);
      }
    }
    return { data, error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // ✅ Guardrail (localhost / WebViews):
    // If there is a stale/corrupt local session, Supabase may immediately attempt a refresh
    // and emit a "TOKEN_REFRESHED with null session" → the app looks like it logs in and instantly logs out.
    // Clear local auth state before signing in.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // ignore
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error && data.user) {
      try {
        await ensureUserProfile(data.user);
      } catch (profileError) {
        console.warn('[AuthProvider] No se pudo asegurar perfil tras login:', profileError);
      }

      // 🔁 Invalida todos los perfiles justo después del login para forzar refetch
      await qc.invalidateQueries({ queryKey: ["profile"] });
      await qc.invalidateQueries({ queryKey: ["profile", "me", data.user.id] });
      // Invalidar queries de todos los tipos de perfil
      await qc.invalidateQueries({ queryKey: ["academy"] });
      await qc.invalidateQueries({ queryKey: ["academy", "my"] });
      await qc.invalidateQueries({ queryKey: ["academy", "mine"] });
      await qc.invalidateQueries({ queryKey: ["organizer"] });
      await qc.invalidateQueries({ queryKey: ["organizer", "mine"] });
      await qc.invalidateQueries({ queryKey: ["teacher"] });
      await qc.invalidateQueries({ queryKey: ["teacher", "mine"] });
      await qc.invalidateQueries({ queryKey: ["brand"] });
      await qc.invalidateQueries({ queryKey: ["brand", "mine"] });
      
      // 🎭 Resetear modo de perfil a "usuario" por defecto
      useProfileMode.getState().setMode("usuario");
    }
    
    return { data, error };
  }, [qc]);

  const signOut = useCallback(async () => {
    try {
      // ✅ ACTUALIZAR ESTADO LOCAL INMEDIATAMENTE (antes de llamar a signOut)
      // Esto asegura que la UI se actualice de forma instantánea
      setSession(null);
      setUser(null);
      setLoading(false);
      
      // 🧹 Limpiar cache INMEDIATAMENTE
      qc.clear();
      
      // 🎭 Resetear modo de perfil a "usuario"
      useProfileMode.getState().setMode("usuario");
      
      // 🔐 Cerrar sesión en Supabase (intentar global primero, luego local como fallback)
      let error: any = null;
      try {
        // Intentar logout global primero (cierra sesión en todos los dispositivos)
        const { error: globalError } = await supabase.auth.signOut({ scope: 'global' });
        if (globalError) {
          // Si falla, intentar logout local
          const { error: localError } = await supabase.auth.signOut({ scope: 'local' });
          error = localError;
        }
      } catch (signOutError: any) {
        error = signOutError;
        console.warn('[AuthProvider] Error en signOut de Supabase (continuando con logout local):', signOutError);
      }
      
      // Si hay error, solo loguearlo pero no fallar (ya limpiamos el estado local)
      if (error) {
        console.warn('[AuthProvider] Logout warning (puede ignorarse, estado local ya limpiado):', error);
      }

      // ✅ Best-effort: notify native host to clear any SDK session (Google)
      try {
        if (isMobileWebView()) {
          const rn = (window as any).ReactNativeWebView;
          rn?.postMessage?.(JSON.stringify({ type: 'NATIVE_SIGN_OUT' }));
        }
      } catch {
        // ignore
      }
      
      return { error: null }; // Siempre retornar success porque el estado local ya está limpio
    } catch (e: any) {
      console.error('[AuthProvider] Logout error:', e);
      // ✅ Asegurar que el estado local se limpie incluso si hay excepción
      setSession(null);
      setUser(null);
      setLoading(false);
      qc.clear();
      useProfileMode.getState().setMode("usuario");
      return { error: e };
    }
  }, [qc]);

  const value = useMemo(
    () => ({
      session,
      user,
      loading,
      signUp,
      signIn,
      signOut,
    }),
    [session, user, loading, signUp, signIn, signOut],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
    </Ctx.Provider>
  );
}

/** Hook oficial. Devuelve el contexto de auth (default inerte si se usa fuera del Provider). */
export function useAuth(): AuthCtx {
  return useContext(Ctx);
}

// Export default para tolerar ambos estilos de import
export default useAuth;
